/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import constants from '../constants';

import type { NodeProps } from './types';
import TreeNode from './treenode';
import WhenNode from './whennode';
import Utils from './utils';

const { FIELD, WHEN, DECLARATIVE_TARGET, PAGELIST } = constants.CONTEXT_TREE_NODE_TYPES;
class ContextNode {
  readonly type: string;
  readonly name: string;
  readonly children: TreeNode[];
  private readonly eventSubscribers: ((object: { type: string }) => void)[];
  public readonly references: Map<string, Map<string, TreeNode | WhenNode>>;
  public props: NodeProps;

  constructor(type: string, name: string, options: NodeProps = {}) {
    this.type = type;
    this.name = name;
    this.children = [];
    this.eventSubscribers = [];
    this.props = options;
    this.references = new Map([
      [FIELD, new Map()],
      [WHEN, new Map()],
      [DECLARATIVE_TARGET, new Map()]
    ]);
  }

  /**
   * @description This method adds the node to the set of references present in the current node
   * with the name as its key
   * @example <caption>Example for addReference()</caption>
   * // In this example, the API adds the reference to the current node
   * this.addReference("TOP_EmbeddedData_5.EmbeddedData",fieldNode)
   * // returns  -
   * @param {string} name name of the reference to be added
   * @param {TreeNode} node node to be added as a reference
   * @returns {void}
   * @function
   * @private
   */
  addReference(name: string, node: TreeNode): void {
    if (this.references.has(node.type)) {
      const previousReferences = this.references.get(node.type) as Map<string, TreeNode>;
      previousReferences.set(name, node);
      this.references.set(node.type, previousReferences);
    } else {
      const nodeType = node.type;
      if (nodeType === PAGELIST) {
        const previousReferences = this.references.get(FIELD) as Map<string, TreeNode>;
        previousReferences.set(name, node);
        this.references.set(FIELD, previousReferences);
      }
    }
  }

  /**
   * @description - This method removes the pagelist field references from the ContextNode.
   * @param name - Name of the pagelist field
   * @param length - Length of pagelist field value which came from server.
   * @example <caption>Example for removePageListNodes()</caption>
   * // In this example, the API removed the references of pagelist field nodes from context node if there are
   * any pageList field entries present with length greater than 3.
   * this.removePageListNodes("caseInfo.content.Employees",3)
   */
  removePageListNodes(name: string, newLength: number) {
    const pageListNodes = this.children
      .filter((node: TreeNode) => node.name.startsWith(name))
      .filter((node: TreeNode) => {
        const pageListIndex = Utils.getPageListIndex(node.name, name);
        if (pageListIndex !== -1) {
          return +pageListIndex >= newLength;
        }
        return false;
      });
    if (pageListNodes.length > 0) {
      pageListNodes.forEach((node: TreeNode) => {
        this.removeChildNode(node.name, newLength);
      });
    }
  }

  /**
   * @description - This method removes the each pagelist field references from the ContextNode.
   * @param name - Name of the pagelist field
   * @param length - Length of pagelist field value which came from server.
   * @example <caption>Example for removeChildNode()</caption>
   * // In this example, the API removed the references of pagelist field nodes from context node if there are
   * any pageList field entries present with length greater than 3.
   * this.removeChildNode("caseInfo.content.Employees[0]",3)
   */
  removeChildNode(name: string, newLength: number): void {
    const index = this.children.findIndex((node: TreeNode) => node.name.startsWith(name));
    if (index !== -1) {
      const [childNode] = this.children.splice(index, 1);
      const parentNode = childNode?.parent as TreeNode;
      parentNode?.removeChildNode(name);
    }
    const fieldReferences = this.references.get(FIELD) as Map<string, TreeNode>;
    const declarativeTargetReferences = this.references.get(DECLARATIVE_TARGET) as Map<string, TreeNode>;
    [...fieldReferences.keys(), ...declarativeTargetReferences.keys()]
      .filter((val: string) => val.startsWith(name))
      .filter((val: string) => {
        const [, , pageListIndex] = Utils.getPageListNameAndIndexFromPageName(val) as string[];
        return +pageListIndex >= newLength;
      })
      .forEach((val: string) => {
        const node: TreeNode = fieldReferences.get(val) as TreeNode;
        const parent = node?.parent as TreeNode | undefined;
        parent?.removeChildren();
        fieldReferences.delete(val);
      });
  }

  /**
   * @description This method gives the reference for name provided.
   * @example <caption>Example for getReference()</caption>
   * // In this example, the API gives the reference to the node we are looking for
   * viewNode = pageNode.getReference(parentView)
   * // returns  - Tree node which was set as reference
   * @param {string} name nameof node
   * @param {string} type - type of node
   * @returns {TreeNode} the reference node based on the name
   * @function
   * @private
   */
  getReference(name: string, type: string): WhenNode | TreeNode | undefined {
    if (this.references.has(type)) {
      return this.references.get(type)?.get(name);
    }
    return undefined;
  }

  /**
   * @description This method gives the node for type, name provided.
   * @example <caption>Example for getNode()</caption>
   * // In this example, the API gives the node for the type, name are looking for
   * node = this.getContextNode(contextName)?.getNode('PAGE', pageName)
   * // returns  - The node based on type, name provided
   * @param {string} type type of node
   * @param {string} name name of node
   * @returns {TreeNode} - The node based on type, name provided
   * @function
   * @private
   */
  getNode(type: string, name: string, index?: number): TreeNode | undefined {
    return this.children.find(
      (node: TreeNode) => node.type === type && node.name === name && node.props.index === index
    );
  }

  /**
   * @description This method adds the node for type, name provided.
   * @example <caption>Example for addNode()</caption>
   * // In this example, the API adds the node for the type, name are looking for
   * const pageNode = this.addContextNode(contextName).addNode('PAGE', pageName);
   * // returns  - The node based on type, name provided
   * @param {string} type type of node
   * @param {string} name name of node
   * @returns {TreeNode} - The added node based on type, name provided
   * @function
   * @private
   */
  addNode(type: string, name: string, options: NodeProps = {}): TreeNode {
    let node = this.getNode(type, name, options.index);
    if (node) {
      node.props = options;
    } else {
      node = new TreeNode(type, name, options);
      node.parent = this;
      this.children.push(node);
    }
    return node;
  }

  /**
   * @description This method adds the evenlistener for callback provided.
   * @example <caption>Example for addEventListener()</caption>
   * // In this example, the API adds the callback to the event subscribers
   * this.getPageListNode(contextName, pageName, viewName, pageListName)?.addEventListener(callback)
   * // returns  -
   * @param {callback} callback function to be called on mutation
   * @returns {void} -
   * @function
   * @private
   */
  addEventListener(callback: (object: { type: string }) => void) {
    this.eventSubscribers.push(callback);
  }

  /**
   * @description This method triggers the event for the event object provided.
   * @example <caption>Example for triggerEvent()</caption>
   * // In this example, the API triggers the event for the event object
   * contextNode.getReference(`${pageName}.${pageListName}`)?.triggerEvent(mutateObject);
   * // returns  -
   * @param {Object} eventObject mutation objects to be passed to the listeners
   * @param {boolean} stopPropagation stop propagating the event to parent
   * @returns {TreeNode} -
   * @function
   * @private
   */
  triggerEvent(eventObject: { type: string }): void {
    this.eventSubscribers.forEach((callback: (object: { type: string }) => void) => {
      callback(eventObject);
    });
  }

  removeReference(name: string, type: string, index?: number) {
    const fieldMap = this.references.get(type) as Map<string, TreeNode>;
    const reference = fieldMap?.get(name);
    if (reference?.props?.index === index) {
      fieldMap.delete(name);
    }
  }

  removeNode(name: string, index: number) {
    for (let i = this.children.length - 1; i >= 0; i -= 1) {
      const node = this.children[i];

      if (node.props.index === index && node.name === name) {
        this.children.splice(i, 1);
        break;
      }
    }
  }

  addWhen(pageName: string, subType: string, referenceName: string, whenName: string, options?: NodeProps) {
    if (!referenceName || !whenName || !pageName || !subType) {
      return;
    }
    if (referenceName.startsWith('.')) {
      referenceName = referenceName.substring(1);
    }
    const key = `${pageName}.${referenceName}.${whenName}`;
    const whenNode = new WhenNode(subType, referenceName, whenName, options);
    const previousWhensMap = this.references.get('WHEN');
    previousWhensMap?.set(key, whenNode);
  }
}
export default ContextNode;
